import axios from 'axios';
import store from '../store/store';
import { logout, setTokens } from '../store/slices/authSlice';
import { toastError } from '../utils/toast';
import { acceptLanguage } from '../i18n';
import {
  createRequestHaltedError,
  haltRequests,
  isRequestHalted,
  shouldHaltForStatus,
} from '../utils/requestHalt';

const baseURL = process.env.REACT_APP_API_BASE_URL || '/api';

const normalizeToken = (token) => {
  if (typeof token !== 'string') return null;
  const trimmed = token.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return null;
  return trimmed;
};

const api = axios.create({ baseURL });

api.interceptors.request.use(
  (config) => {
    if (isRequestHalted()) {
      return Promise.reject(createRequestHaltedError());
    }

    const token = normalizeToken(localStorage.getItem('accessToken'));
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // The backend localizes its responses by this header (RFC_I18N R10).
    config.headers['Accept-Language'] = acceptLanguage();
    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  console.log('PROCESSQ');
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (shouldHaltForStatus(status)) {
      const didHalt = haltRequests({
        status,
        message: error.response?.data?.message,
        source: 'axios',
      });
      if (didHalt) {
        toastError(error);
      }
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      const refreshToken = normalizeToken(localStorage.getItem('refreshToken'));
      if (!refreshToken) {
        store.dispatch(logout()); // Выход, если нет refreshToken
        return Promise.reject(error);
      }

      return new Promise((resolve, reject) => {
        axios
          .post(`${baseURL}/auth/refresh`, { refreshToken })
          .then(({ data }) => {
            const nextAccessToken = normalizeToken(data?.accessToken);
            const nextRefreshToken = normalizeToken(data?.refreshToken) || refreshToken;

            if (!nextAccessToken) {
              throw new Error('Refresh endpoint did not return accessToken');
            }

            localStorage.setItem('accessToken', nextAccessToken);
            localStorage.setItem('refreshToken', nextRefreshToken);
            api.defaults.headers.common['Authorization'] = 'Bearer ' + nextAccessToken;
            store.dispatch(setTokens({ accessToken: nextAccessToken, refreshToken: nextRefreshToken, user: store.getState().auth.user }));
            processQueue(null, nextAccessToken);
            resolve(api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            store.dispatch(logout()); // Выход при неудачном обновлении
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }
    // Вторичный 401 после попытки refresh — считаем сессию недействительной
    if (error.response && error.response.status === 401 && originalRequest?._retry) {
      store.dispatch(logout());
    }
    // Handle 403 Forbidden - redirect to home
    if (error.response && error.response.status === 403) {
      window.location.href = '/';
      return Promise.reject(error);
    }
    toastError(error);
    return Promise.reject(error);
  },
);

export default api;
